(ns frontend.views.map
  (:require
   [common.map             :as cmap]
   [common.util            :as u]
   [opengb.i18n            :refer [Tr]]
   [reagent.core           :as rg]
   [reagent.dom.client     :as rg-dom-client]
   [reagent.dom.server     :as rg-dom-server]
   [re-frame.core          :as rf]
   [shadow.graft           :as graft]
   [tailwind-hiccup.core   :refer [tw]]))

;; Events

(rf/reg-event-fx :frontend.views.map/setup-db
                 (fn [{:keys [db] :as _cofx} [_ config]]
                   {:db (merge db config)}))

(rf/reg-event-fx :frontend.views.map/map-initialized
                 (fn [{:keys [db] :as _cofx} [_ ml-map-reference]]
                   {:db (assoc db :frontend.views.map/ml-map ml-map-reference)}))

(rf/reg-event-fx :frontend.views.map/set-active-popup
                 (fn [{:keys [db] :as _cofx} [_ active-popup]]
                   {:db (assoc db :frontend.views.map/active-popup active-popup)}))

(rf/reg-event-fx :frontend.views.map/map-start-loading
                 (fn [{:keys [db] :as _cofx} _]
                   {:db (assoc db :frontend.views.map/map-is-loading? true)
                    :frontend.telemetry/start-span!
                    [:frontend.views.map/map-loading
                     {:current-filters (:frontend.views.public/current-filters db)}]}))

(rf/reg-event-fx :frontend.views.map/map-finish-loading
                 (fn [{:keys [db] :as _cofx} _]
                   {:db                           (assoc db :frontend.views.map/map-is-loading? false)
                    :frontend.telemetry/end-span! [:frontend.views.map/map-loading]}))

(rf/reg-event-fx :frontend.views.map/focus-on-building
                 (fn [{:keys [db] :as _cofx} [_ js-coords]]
                   {:frontend.views.map/focus-on-coordinates
                    [(:frontend.views.map/ml-map db) js-coords]}))

;; Effects

(rf/reg-fx :frontend.views.map/focus-on-coordinates
           (fn [[^js/maplibregl.Map ml-map js-coords]]
             (.resize ml-map)
             (.flyTo ml-map (clj->js {:center js-coords
                                      :zoom cmap/BUILDING-ZOOM}))))

;; Subscriptions

(rf/reg-sub :frontend.views.map/ml-map
            (fn [db _] (:frontend.views.map/ml-map db)))

(rf/reg-sub :frontend.views.map/map-args
            (fn [db _]
              {:buildings-tiles-url       (:frontend.views.map/buildings-tiles-url db)
               :current-filters           (:frontend.views.public/current-filters db)
               :map-config                (:frontend.views.map/map-config db)
               :color-ramp            (:frontend.views.map/color-ramp db)
               :dim-unselected-buildings? (:frontend.views.map/dim-unselected-buildings? db)
               :ml-map                    (:frontend.views.map/ml-map db)
               :property-rank-feature-key (:frontend.views.map/property-rank-feature-key db)
               :selected-building         (:frontend.views.public/selected-building db)
               :show-hover-popup?         (:frontend.views.map/show-hover-popup? db)
               :tenant-id                 (:frontend.views.map/tenant-id db)}))

(rf/reg-sub :frontend.views.map/active-popup
            (fn [db _] (:frontend.views.map/active-popup db)))

(rf/reg-sub :frontend.views.map/map-is-loading?
            (fn [db _] (:frontend.views.map/map-is-loading? db)))

;; Popups

(defn hover-popup:BuildingPopup
  [{:keys [address] :as _building}]
  [:div (tw [:text-neutral-2 :text-base :p-2])
   address])

(defn hover-popup:create-popup-html
  "Renders a BuildingPopup view to html string so it can be displayed by
  maplibre."
  [building]
  (rg-dom-server/render-to-string [hover-popup:BuildingPopup building]))

(defn hover-popup:open-popup
  "Opens popup displaying home address when marker is hovered."
  [e]
  (let [{:keys [active-popup-ref active-popup-id]}
        @(rf/subscribe [:frontend.views.map/active-popup])
        ^js/Object feature (aget (. e -features) 0)
        building    (js->clj (.-properties feature) {:keywordize-keys true})
        building-id (:id building)]
    (when (not= active-popup-id building-id)
      (when active-popup-ref
        (.remove active-popup-ref))
      (let [coordinates (.. feature -geometry -coordinates slice)
            popup-html  (hover-popup:create-popup-html building)
            new-popup   (js/maplibregl.Popup. (clj->js {:closeButton false}))
            ml-map      @(rf/subscribe [:frontend.views.map/ml-map])]
        (-> new-popup
            (.setLngLat coordinates)
            (.setHTML popup-html)
            (.addTo ml-map))
        (rf/dispatch-sync [:frontend.views.map/set-active-popup
                           {:active-popup-id  building-id
                            :active-popup-ref new-popup}])))))

(defn hover-popup:close-popup
  "Closes hover popup."
  [_e]
  (let [{:keys [active-popup-ref]}
        @(rf/subscribe [:frontend.views.map/active-popup])]
    (when active-popup-ref
      (.remove active-popup-ref)
      (rf/dispatch-sync [:frontend.views.map/set-active-popup nil]))))

;; Map

(defn map:handle-marker-click
  "Updates home detail view when marker is clicked."
  [e]
  (let [^js/Object feature (aget (. e -features) 0)
        building-id (.. feature -properties -id)
        js-coords   (.. feature -geometry -coordinates slice)]
    (.log js/console "marker clicked" (.-properties feature))
    (rf/dispatch [:frontend.views.public/set-selected-building
                  building-id js-coords])
    (rf/dispatch [:frontend.views.map/map-start-loading])))

(defn map:set-cursor
  "Sets cursor to provided style."
  [cursor-style _e]
  (let [^js/maplibregl.Map ml-map @(rf/subscribe [:frontend.views.map/ml-map])]
    (set! (.. ml-map getCanvas -style -cursor) cursor-style)))

(defn map:handle-idle
  "Dispatches map-finish-loading event once buildings layer is idle."
  [_e]
  (rf/dispatch [:frontend.views.map/map-finish-loading]))

(defn map:reagent-render
  "Renders a container for the map to be instantiated into."
  []
  [:div#map-container (tw [:w-full :h-full])])

(defn- map-args->url-cfg
  [map-args]
  (select-keys map-args [:buildings-tiles-url :tenant-id]))

(defn map:component-did-mount
  "Instantiates the map and adds event handlers."
  [this]
  (let [[_ {:keys [current-filters map-config show-hover-popup?] :as map-args}] (rg/argv this)
        url-cfg (map-args->url-cfg map-args)
        new-map (-> map-config
                    (assoc-in [:style :sources "buildings-source" :tiles]
                              [(cmap/filters->url url-cfg current-filters)])
                    (clj->js)
                    (js/maplibregl.Map.))]
    (doto new-map
      (.on "click"      "buildings" map:handle-marker-click)
      (.on "mouseenter" "buildings" (partial map:set-cursor "pointer"))
      (.on "mouseleave" "buildings" (partial map:set-cursor ""))
      (.on "idle"       map:handle-idle))
    (when show-hover-popup?
      (doto new-map
        (.on "mousemove"  "buildings" hover-popup:open-popup)
        (.on "mouseleave" "buildings" hover-popup:close-popup)
        (.on "zoom"       "buildings" hover-popup:close-popup)))
    (rf/dispatch-sync [:frontend.views.map/map-initialized new-map])))

(defn map:update-filters
  [^js/maplibregl.Map ml-map url-cfg current-filters]
  (let [filtered-url (cmap/filters->url url-cfg current-filters)
        current-url  (-> ml-map
                         (.getSource "buildings-source")
                         .-tiles
                         first)]
    (when (and current-url
               (not= filtered-url current-url))
      (do
        (-> ml-map
            (.getSource "buildings-source")
            (.setTiles (clj->js [filtered-url])))
        (rf/dispatch [:frontend.views.map/map-start-loading])))
    ml-map))

(defn map:update-buildings
  [^js/maplibregl.Map ml-map building-selected? property-rank-feature-key color-ramp dim-unselected-buildings?]
  (let [ramp          (if (and building-selected? dim-unselected-buildings?)
                        (:map-dim color-ramp)
                        (:map color-ramp))
        circle-color  (cmap/make-color-by-percentile-expression
                       property-rank-feature-key
                       ramp)
        current-color (-> ml-map
                          (.getPaintProperty "buildings" "circle-color")
                          js->clj)]
    (when (not= circle-color current-color)
      (.setPaintProperty ml-map "buildings" "circle-color"
                         (clj->js circle-color)))
    ml-map))

(defn map:update-selected-building
  [^js/maplibregl.Map ml-map url-cfg selected-building]
  (let [selected-visibility (if selected-building "visible" "none")
        selected-url        (cmap/selection->url url-cfg selected-building)
        current-visibility  (.getLayoutProperty ml-map
                                                "selected-building"
                                                "visibility")
        current-url         (-> ml-map
                                (.getSource "selected-building-source")
                                .-tiles
                                first)
        update-visibility?  (not= current-visibility selected-visibility)
        update-url?         (and current-url
                                 (not= current-url selected-url))]
    (when update-visibility?
      (-> ml-map
          (.setLayoutProperty "selected-building"
                              "visibility"
                              selected-visibility)))
    (when update-url?
      (-> ml-map
          (.getSource "selected-building-source")
          (.setTiles (clj->js [selected-url]))))
    ml-map))

(defn map:component-did-update
  "Updates buildings tile source url (which re-renders the building markers) as
  filters change."
  [this]
  (let [[_ {:keys [current-filters
                   color-ramp
                   dim-unselected-buildings?
                   ^js/maplibregl.Map ml-map
                   property-rank-feature-key
                   selected-building] :as map-args}] (rg/argv this)
        url-cfg (map-args->url-cfg map-args)]
    (-> ml-map
        (map:update-buildings selected-building property-rank-feature-key color-ramp dim-unselected-buildings?)
        (map:update-filters url-cfg current-filters)
        (map:update-selected-building url-cfg selected-building))))

(defn MapLibreMap
  "Reagent component that displays a maplibre map."
  [_map-args]
  (rg/create-class {:reagent-render       map:reagent-render
                    :component-did-mount  map:component-did-mount
                    :component-did-update map:component-did-update}))

(defn Map
  "Wrapper to pass the map-args subscription to the MapLibreMap."
  []
  (let [*map-args (rf/subscribe [:frontend.views.map/map-args])]
    [MapLibreMap @*map-args]))

(defmethod graft/scion (u/->scion-id :frontend.views.map/initalize-map)
  [map-config container]
  (let [root (rg-dom-client/create-root container)]
    (rf/dispatch [:frontend.views.map/setup-db map-config])
    (rg-dom-client/render root [Map])))

(defn MapLoading
  "Indicator shown while map is loading."
  []
  (let [*is-loading? (rf/subscribe [:frontend.views.map/map-is-loading?])
        loading?     @*is-loading?]
    [:div (tw [:absolute :inset-0 :flex
               :justify-center :items-center
               "bg-neutral-10/50" :pointer-events-none
               :transition-opacity]
              (if loading?
                ^:tw [:opacity-100]
                ^:tw [:opacity-0])
              {:role         "status"
               :aria-live    "polite"
               :aria-hidden  (if loading? "false" "true")})
     [:img (tw [:w-12]
               {:src "/img/tail-spin.svg" :alt ""})]
     (when loading?
       [:span (tw [:sr-only]) [Tr :frontend.views.map/map-loading]])]))

(defmethod graft/scion (u/->scion-id :frontend.views.map/initalize-map-loading)
  [_map-config container]
  (let [root (rg-dom-client/create-root container)]
    (rg-dom-client/render root [MapLoading])))
